import { NextRequest, NextResponse } from "next/server";
import { JWTPayload, jwtVerify } from "jose";
import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { authKey, authStore } from "@/app/modules/config/General";
import { UserInterface } from "@/app/modules/services/validators/models.interface";

const JWT_SECRET = new TextEncoder().encode(process.env.JWT_SECRET); // Use TextEncoder for the secret key

const PUBLIC_PATHS = ["/auth", "/public"];
const ROLES = { ADMIN: 1, USER: 2 };

async function verifyJwt(token: string): Promise<JWTPayload> {
  try {
    const { payload } = await jwtVerify(token, JWT_SECRET);
    return payload;
  } catch {
    throw new Error("Invalid or expired token");
  }
}

export async function middleware(req: NextRequest) {
  const { pathname } = req.nextUrl;

  // Allow public routes
  if (PUBLIC_PATHS.some((path) => pathname.includes(path))) {
    return NextResponse.next();
  }

  const authHeader = req.headers.get("authorization");

  if (pathname.startsWith("/api")) {
    if (!authHeader?.startsWith("Bearer ")) {
      return NextResponse.json(
        {
          status: 401,
          message: "Missing or invalid authorization header",
          success: false,
        },
        { status: 401 },
      );
    }

    const token = authHeader.split(" ")[1];

    try {
      const payload: JWTPayload = await verifyJwt(token);

      const requestHeaders = new Headers(req.headers);

      requestHeaders.set("userId", String(payload.userId));
      requestHeaders.set("email", String(payload.email));
      requestHeaders.set("role", String(payload.role));

      if (pathname.includes("/user/profile")) {
        return NextResponse.next({
          headers: requestHeaders,
        });
      }
      if (pathname.includes("/admin") && payload.role !== ROLES.ADMIN) {
        return new NextResponse(
          JSON.stringify({
            status: 403,
            message: "Forbidden: Admin access only",
            success: false,
          }),
          { status: 403, headers: { "Content-Type": "application/json" } },
        );
      }

      if (!pathname.includes("/admin") && payload?.role !== ROLES.USER) {
        return new NextResponse(
          JSON.stringify({
            status: 403,
            message: "Forbidden: User access only",
            success: false,
          }),
          { status: 403, headers: { "Content-Type": "application/json" } },
        );
      }

      return NextResponse.next({
        request: {
          headers: requestHeaders,
        },
      });
    } catch (err: any) {
      console.error("JWT Verification Error:", err);
      return NextResponse.json(
        {
          status: 401,
          message: err.message || "Unauthorized access",
          success: false,
        },
        { status: 401 },
      );
    }
  }

  if (pathname.startsWith("/app")) {
    try {
      // Fetch the cookies
      const cookieKey = "_KeywordToPinAuth_state";
      const cookieStore = await cookies();

      // Get the cookie and handle potential null/undefined values
      const authStateCookie = cookieStore.get(cookieKey);

      if (!authStateCookie || !authStateCookie.value) {
        return NextResponse.redirect(
          new URL(
            `/auth/signin?redirect=${encodeURIComponent(pathname)}`,
            req.nextUrl,
          ),
          { status: 301 },
        );
      }

      // Parse the cookie value safely
      let user: any | null = null;
      try {
        const parsedCookie = JSON.parse(authStateCookie.value);
        user = parsedCookie?.value ?? parsedCookie; // Handle both nested and direct structures
      } catch (parseError) {
        console.error("Error parsing auth cookie:", parseError);
        return NextResponse.redirect(
          new URL(`/auth/signin?error=invalid_session`, req.nextUrl),
          { status: 301 },
        );
      }

      if (!user) {
        return NextResponse.redirect(
          new URL(
            `/auth/signin?redirect=${encodeURIComponent(pathname)}`,
            req.nextUrl,
          ),
          { status: 301 },
        );
      }

      // Admin access restriction
      if (pathname.includes("/app/profile")) {
        return NextResponse.next();
      }

      if (pathname.includes("/admin") && user?.role !== ROLES.ADMIN) {
        return NextResponse.redirect(new URL("/app/404", req.nextUrl), {
          status: 301,
        });
      }

      // User access restriction
      if (!pathname.includes("/admin") && user?.role !== ROLES.USER) {
        return NextResponse.redirect(new URL("/app/admin/404", req.nextUrl), {
          status: 301,
        });
      }

      return NextResponse.next();
    } catch (error: any) {
      console.error("Error in app middleware:", error);

      // Handle middleware errors gracefully - redirect to signin instead of showing error
      return NextResponse.redirect(
        new URL(`/auth/signin?error=middleware_error`, req.nextUrl),
        { status: 301 },
      );
    }
  }

  return NextResponse.next();
}

export const config = {
  matcher: ["/api/((?!auth|public).*)", "/app/:path*"],
};
